import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { toast } from 'react-toastify';

const Container = styled.div`
  padding: 2rem;
  max-width: 1000px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const BackLink = styled(Link)`
  color: #005eb8;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const FormCard = styled.form`
  background: #f8f9fa;
  border: 1px solid #e0e4e8;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-end;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const Label = styled.label`
  font-size: 0.8rem;
  font-weight: bold;
  color: #444;
`;

const Input = styled.input`
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.9rem;
  text-transform: ${p => (p.$uppercase ? 'uppercase' : 'none')};
`;

const Select = styled.select`
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.9rem;
`;

const Button = styled.button`
  padding: 9px 18px;
  background-color: #005eb8;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  &:hover { background-color: #004a94; }
  &:disabled { background-color: #99b9d6; cursor: not-allowed; }
`;

const DangerButton = styled(Button)`
  background-color: transparent;
  color: #d9534f;
  border: 1px solid #d9534f;
  padding: 4px 10px;
  font-size: 0.85rem;
  &:hover { background-color: #d9534f; color: #fff; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th, td { padding: 10px 12px; border-bottom: 1px solid #eee; text-align: left; vertical-align: middle; }
  th { background-color: #f9f9f9; font-size: 0.85rem; color: #555; }
`;

const CodeChip = styled.span`
  font-family: monospace;
  font-weight: bold;
  background: #eef3f9;
  padding: 2px 8px;
  border-radius: 4px;
`;

const StatusPill = styled.span`
  display: inline-block;
  font-size: 0.75rem;
  font-weight: bold;
  padding: 2px 10px;
  border-radius: 999px;
  color: ${p => (p.$tone === 'active' ? '#1a7a4c' : p.$tone === 'scheduled' ? '#005eb8' : '#8a8a8a')};
  background: ${p => (p.$tone === 'active' ? '#e4f5eb' : p.$tone === 'scheduled' ? '#e4eefb' : '#eee')};
`;

const HelpText = styled.p`
  font-size: 0.8rem;
  color: #777;
  margin: -1rem 0 1.5rem;
`;

const getStatus = (c) => {
  const now = new Date();
  if (c.startsAt && now < new Date(c.startsAt)) return { label: 'Scheduled', tone: 'scheduled' };
  if (c.endsAt && now > new Date(c.endsAt)) return { label: 'Expired', tone: 'expired' };
  if (c.maxRedemptions != null && (c.timesRedeemed || 0) >= c.maxRedemptions) return { label: 'Used up', tone: 'expired' };
  return { label: 'Active', tone: 'active' };
};

const formatValue = (c) => (c.type === 'percent' ? `${c.value}% off` : `£${Number(c.value).toFixed(2)} off`);

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

// <input type="date"> works in local time with no time component; store/read
// as plain YYYY-MM-DD and let the backend turn it into an ISO instant.
const toDateInputValue = (iso) => (iso ? iso.slice(0, 10) : '');

const emptyForm = { code: '', type: 'percent', value: '', startsAt: '', endsAt: '', maxRedemptions: '' };

const DiscountCodes = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('adminToken');
  const api = useMemo(() => axios.create({
    baseURL: process.env.REACT_APP_BACKEND_URL,
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  useEffect(() => {
    if (!token) navigate('/');
  }, [token, navigate]);

  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admins/discount-codes');
      setCodes(res.data.codes);
    } catch (err) {
      toast.error('Failed to load discount codes.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.value) {
      toast.error('Enter a code and a value.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/admins/discount-codes', {
        code: form.code,
        type: form.type,
        value: Number(form.value),
        startsAt: form.startsAt || null,
        endsAt: form.endsAt || null,
        maxRedemptions: form.maxRedemptions || null,
      });
      toast.success(`Code "${form.code.toUpperCase()}" created.`);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create discount code.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete discount code "${code}"? This can't be undone.`)) return;
    try {
      await api.delete(`/admins/discount-codes/${code}`);
      toast.success(`Code "${code}" deleted.`);
      setCodes(prev => prev.filter(c => c.code !== code));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete discount code.');
    }
  };

  const handleDateChange = async (code, field, value) => {
    setCodes(prev => prev.map(c => c.code === code ? { ...c, [field]: value ? new Date(value).toISOString() : null } : c));
    try {
      await api.put(`/admins/discount-codes/${code}`, { [field]: value || null });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update dates.');
      load();
    }
  };

  return (
    <Container>
      <Header>
        <h1>Discount Codes</h1>
        <BackLink to="/dashboard">&larr; Back to Dashboard</BackLink>
      </Header>

      <FormCard onSubmit={handleCreate}>
        <Field>
          <Label htmlFor="dc-code">Code</Label>
          <Input id="dc-code" $uppercase placeholder="SUMMER10" value={form.code} onChange={e => handleChange('code', e.target.value)} />
        </Field>
        <Field>
          <Label htmlFor="dc-type">Type</Label>
          <Select id="dc-type" value={form.type} onChange={e => handleChange('type', e.target.value)}>
            <option value="percent">Percentage off</option>
            <option value="fixed">Fixed amount off</option>
          </Select>
        </Field>
        <Field>
          <Label htmlFor="dc-value">{form.type === 'percent' ? 'Percent (%)' : 'Amount (£)'}</Label>
          <Input id="dc-value" type="number" min="0" step={form.type === 'percent' ? '1' : '0.01'} value={form.value} onChange={e => handleChange('value', e.target.value)} style={{ width: '110px' }} />
        </Field>
        <Field>
          <Label htmlFor="dc-start">Runs from (optional)</Label>
          <Input id="dc-start" type="date" value={form.startsAt} onChange={e => handleChange('startsAt', e.target.value)} />
        </Field>
        <Field>
          <Label htmlFor="dc-end">Runs until (optional)</Label>
          <Input id="dc-end" type="date" value={form.endsAt} onChange={e => handleChange('endsAt', e.target.value)} />
        </Field>
        <Field>
          <Label htmlFor="dc-max">Usage limit (optional)</Label>
          <Input id="dc-max" type="number" min="1" step="1" placeholder="Unlimited" value={form.maxRedemptions} onChange={e => handleChange('maxRedemptions', e.target.value)} style={{ width: '110px' }} />
        </Field>
        <Button type="submit" disabled={saving}>{saving ? 'Adding...' : 'Add Code'}</Button>
      </FormCard>
      <HelpText>Leave "Runs from" / "Runs until" / "Usage limit" blank for a code with no start date, end date, or cap on how many times it can be used. Codes are matched without case sensitivity.</HelpText>

      {loading ? (
        <p>Loading discount codes...</p>
      ) : codes.length === 0 ? (
        <p>No discount codes yet — add one above.</p>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Discount</th>
              <th>Runs From</th>
              <th>Runs Until</th>
              <th>Used</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {codes.map(c => {
              const status = getStatus(c);
              return (
                <tr key={c.id}>
                  <td><CodeChip>{c.code}</CodeChip></td>
                  <td>{formatValue(c)}</td>
                  <td>
                    <Input
                      type="date"
                      value={toDateInputValue(c.startsAt)}
                      onChange={e => handleDateChange(c.code, 'startsAt', e.target.value)}
                      title={formatDate(c.startsAt)}
                    />
                  </td>
                  <td>
                    <Input
                      type="date"
                      value={toDateInputValue(c.endsAt)}
                      onChange={e => handleDateChange(c.code, 'endsAt', e.target.value)}
                      title={formatDate(c.endsAt)}
                    />
                  </td>
                  <td>{c.timesRedeemed || 0}{c.maxRedemptions != null ? ` / ${c.maxRedemptions}` : ''}</td>
                  <td><StatusPill $tone={status.tone}>{status.label}</StatusPill></td>
                  <td><DangerButton onClick={() => handleDelete(c.code)}>Delete</DangerButton></td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

export default DiscountCodes;
